import { useMemo } from 'react'
import { useCategories } from '../hooks/useCategories'
import { useUnreadCount } from '../hooks/useConversations'
import { useSidebarConfig } from '../hooks/useSidebarConfig'
import { useAuth } from '../lib/auth'
import { useProfile } from '../hooks/useProfile'
import { getCategoryColor } from '../lib/colors'
import { initialsFromUser } from '../lib/initials'
import type { Tag } from '../hooks/useTags'
import type { Composite } from '../hooks/useComposites'

// Module-level so we don't allocate a fresh Set per render for the membership
// check inside the message-nav map.
const COUNTED_MESSAGE_KEYS = new Set(['INBOX', 'DRAFTS', 'REMINDERS'])

interface SidebarProps {
  activeFilter: string
  onFilterChange: (filter: string) => void
  onNavigate?: (page: 'inbox' | 'templates' | 'settings') => void
  activePage?: string
  searchQuery?: string
  onSearchChange?: (query: string) => void
  onSearchFocus?: () => void
  onSearchEscape?: () => void
  searchMode?: boolean
  unreadCount?: number
  draftCount?: number
  inviteCount?: number
  reminderCount?: number
  hasSalesNav?: boolean
  hasRecruiter?: boolean
  tags?: Tag[]
  tagCounts?: Record<string, number>
  /** Saved Inbox Composites (ADR 0029). Rendered as their own "FILTERS"
   * section in the sidebar. Counts are surfaced by the parent because
   * Shell already does the full conversation-filter pass for the
   * currently-selected filter. */
  composites?: Composite[]
  compositeCounts?: Record<string, number>
  onOrganizeOpen?: () => void
}


const NETWORK_ITEMS = [
  { key: 'INVITES', label: 'Invites', emoji: '🤝', showCount: true },
  { key: 'PENDING', label: 'Pending', emoji: '⏳' },
  { key: 'CONNECTIONS', label: 'Connections', emoji: '👥', showCount: true },
]

export default function Sidebar({
  activeFilter, onFilterChange, onNavigate, activePage = 'inbox',
  searchQuery = '', onSearchChange, onSearchFocus, onSearchEscape, searchMode = false,
  unreadCount: unreadCountProp,
  draftCount: draftCountProp = 0,
  inviteCount: inviteCountProp = 0,
  reminderCount: reminderCountProp = 0,
  hasSalesNav = false,
  hasRecruiter = false,
  tags = [],
  tagCounts = {},
  composites = [],
  compositeCounts = {},
  onOrganizeOpen,
}: SidebarProps) {
  const unreadCountFromDB = useUnreadCount()
  const unreadCount = unreadCountProp ?? unreadCountFromDB
  const { categories } = useCategories()
  const { items: sidebarItems } = useSidebarConfig()
  const { user } = useAuth()
  const { profile, profileLoading } = useProfile()

  // Hold off on deriving initials from the email fallback until the profile
  // query has resolved — otherwise the badge briefly renders email-initials
  // then flips to display-name-initials once the profile request returns,
  // which reads as a visual flicker on every page load.
  const initials = useMemo(() => {
    if (profileLoading) return ''
    return initialsFromUser(profile?.display_name || user?.email) || ''
  }, [profile?.display_name, user?.email, profileLoading])

  // Memoize so downstream memo comparators (e.g. a future memoized nav row)
  // see a stable object when upstream counts haven't changed.
  const counts = useMemo<Record<string, number>>(() => ({
    INBOX: unreadCount ?? 0,
    DRAFTS: draftCountProp,
    REMINDERS: reminderCountProp,
    INVITES: inviteCountProp,
    CONNECTIONS: 0,
  }), [unreadCount, draftCountProp, reminderCountProp, inviteCountProp])

  return (
      <aside className="flex w-[220px] flex-col border-r border-zinc-200 bg-zinc-50">
        {/* Logo */}
        <div className="px-5 pb-4 pt-5">
          <span className="text-[22px] font-bold tracking-tight text-zinc-900" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
            /slash
          </span>
        </div>

        {/* Search */}
        <div className="px-3 pb-3">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange?.(e.target.value)}
            onFocus={() => onSearchFocus?.()}
            onKeyDown={(e) => { if (e.key === 'Escape') onSearchEscape?.() }}
            placeholder="Search conversations..."
            className={`w-full rounded-lg px-3 py-[7px] text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none ${
              searchMode ? 'bg-white ring-1 ring-blue-400' : 'bg-zinc-100'
            }`}
          />
        </div>

        {/* Messages */}
        <nav className="flex flex-col px-2">
          <span className="px-3 pb-[5px] pt-[6px] text-[10px] font-medium tracking-wider text-zinc-400">
            MESSAGES
          </span>
          {sidebarItems
            .filter((item) => item.visible
              && (item.key !== 'SALESNAV' || hasSalesNav)
              && (item.key !== 'RECRUITER' || hasRecruiter))
            .map((item) => {
              const isActive = activeFilter === item.key && activePage === 'inbox'
              const count = counts[item.key]
              const showCount = COUNTED_MESSAGE_KEYS.has(item.key)
              return (
                <button
                  key={item.key}
                  // Single navigate — `onFilterChange` already takes us to a
                  // /filter URL which Shell's `parseRoute` maps to
                  // `activePage: 'inbox'`. Calling `onNavigate?.('inbox')`
                  // here too produced a second `navigate('/inbox')` that
                  // stomped the first, leaving the filter click a no-op
                  // (ADR 0026 single-navigate invariant — fix for a miss
                  // in the original 0.6 rollout).
                  onClick={() => onFilterChange(item.key)}
                  className={`flex items-center justify-between rounded-[7px] px-3 py-[5px] text-left ${
                    isActive
                      ? 'bg-zinc-900 text-white'
                      : 'text-zinc-600 hover:bg-zinc-100'
                  }`}
                >
                  <div className="flex items-center gap-[7px]">
                    <span className="text-[13px]">{item.emoji}</span>
                    <span className={`text-[12.5px] ${isActive ? 'font-medium' : ''}`}>
                      {item.label}
                    </span>
                  </div>
                  {showCount && count > 0 && (
                    isActive ? (
                      <span className="rounded-full bg-blue-500 px-1.5 text-[10px] font-semibold text-white">
                        {count}
                      </span>
                    ) : (
                      <span className="text-[10px] font-medium text-zinc-400">{count}</span>
                    )
                  )}
                </button>
              )
            })}
        </nav>

        {/* Network */}
        <nav className="mt-1.5 flex flex-col px-2">
          <span className="px-3 pb-[5px] pt-[6px] text-[10px] font-medium tracking-wider text-zinc-400">
            NETWORK
          </span>
          {NETWORK_ITEMS.map((item) => {
            const isActive = activeFilter === item.key && activePage === 'inbox'
            const count = counts[item.key]
            return (
              <button
                key={item.key}
                // Single navigate — see rationale above the MESSAGES
                // onClick. Same double-navigate fix (ADR 0026).
                onClick={() => onFilterChange(item.key)}
                className={`flex items-center justify-between rounded-[7px] px-3 py-[5px] text-left ${
                  isActive
                    ? 'bg-zinc-900 text-white'
                    : 'text-zinc-600 hover:bg-zinc-100'
                }`}
              >
                <div className="flex items-center gap-[7px]">
                  <span className="text-[13px]">{item.emoji}</span>
                  <span className="text-[12.5px]">{item.label}</span>
                </div>
                {item.showCount && count > 0 && (
                  <span className="text-[10px] font-medium text-zinc-400">{count}</span>
                )}
              </button>
            )
          })}
        </nav>

        {/* Folders */}
        {categories.length > 0 && (
          <nav className="mt-1.5 flex flex-col px-2">
            <span className="px-3 pb-[5px] pt-[6px] text-[10px] font-medium tracking-wider text-zinc-400">
              FOLDERS
            </span>
            {categories.map((cat) => {
              const isActive = activeFilter === `category:${cat.id}`
              return (
                <button
                  key={cat.id}
                  // Single navigate — see rationale above (ADR 0026 fix).
                  onClick={() => onFilterChange(`category:${cat.id}`)}
                  className={`flex items-center gap-[7px] rounded-[7px] px-3 py-[5px] text-left ${
                    isActive
                      ? 'bg-zinc-900 text-white'
                      : 'text-zinc-600 hover:bg-zinc-100'
                  }`}
                >
                  <div
                    className="h-2 w-2 rounded-full"
                    style={{ backgroundColor: getCategoryColor(cat.color) }}
                  />
                  <span className="text-[12.5px]">{cat.name}</span>
                </button>
              )
            })}
          </nav>
        )}

        {/* Saved Filters (Inbox Composites — ADR 0029). Rendered between
         * Folders and Tags so the "bespoke" filter bucket sits next to
         * the two "organisational" ones. Counts mirror the tag-counts
         * shape — passed down by Shell which already does the full
         * evaluator pass when computing its own conversation list. */}
        {composites.length > 0 && (
          <nav className="mt-1.5 flex flex-col px-2">
            <span className="px-3 pb-[5px] pt-[6px] text-[10px] font-medium tracking-wider text-zinc-400">
              FILTERS
            </span>
            {composites.map((composite) => {
              const isActive = activeFilter === `composite:${composite.id}`
              const count = compositeCounts[composite.id] || 0
              return (
                <button
                  key={composite.id}
                  // Single navigate — see rationale above (ADR 0026 fix).
                  onClick={() => onFilterChange(`composite:${composite.id}`)}
                  className={`flex items-center gap-[7px] rounded-[7px] px-3 py-[5px] text-left ${
                    isActive ? 'bg-zinc-900 text-white' : 'text-zinc-600 hover:bg-zinc-100'
                  }`}
                >
                  {composite.icon ? (
                    <span className="w-3 shrink-0 text-center text-[12.5px] leading-none" aria-hidden="true">{composite.icon}</span>
                  ) : (
                    <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: getCategoryColor(composite.color) }} />
                  )}
                  <span className="flex-1 truncate text-[12.5px]">{composite.name}</span>
                  {count > 0 && (
                    <span className={`text-[10px] font-medium ${isActive ? 'text-zinc-300' : 'text-zinc-400'}`}>{count}</span>
                  )}
                </button>
              )
            })}
          </nav>
        )}

        {/* Tags */}
        {tags.length > 0 && (
          <nav className="mt-1.5 flex flex-col px-2">
            <span className="px-3 pb-[5px] pt-[6px] text-[10px] font-medium tracking-wider text-zinc-400">
              TAGS
            </span>
            {tags.map((tag) => {
              const isActive = activeFilter === `tag:${tag.id}`
              const count = tagCounts[tag.id] || 0
              return (
                <button
                  key={tag.id}
                  // Single navigate — see rationale above (ADR 0026 fix).
                  onClick={() => onFilterChange(`tag:${tag.id}`)}
                  className={`flex items-center gap-[7px] rounded-[7px] px-3 py-[5px] text-left ${
                    isActive ? 'bg-zinc-900 text-white' : 'text-zinc-600 hover:bg-zinc-100'
                  }`}
                >
                  {tag.icon ? (
                    <span className="w-3 shrink-0 text-center text-[12.5px] leading-none" aria-hidden="true">{tag.icon}</span>
                  ) : (
                    <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: getCategoryColor(tag.color) }} />
                  )}
                  <span className="flex-1 truncate text-[12.5px]">{tag.name}</span>
                  {tag.team_id && (
                    <svg
                      width="10" height="10" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                      className={`shrink-0 ${isActive ? 'text-zinc-300' : 'text-zinc-400'}`}
                      aria-label="Shared with team"
                    >
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                  )}
                  {count > 0 && (
                    <span className={`text-[10px] font-medium ${isActive ? 'text-zinc-300' : 'text-zinc-400'}`}>{count}</span>
                  )}
                </button>
              )
            })}
          </nav>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Bottom toolbar */}
        <div className="flex items-center justify-between border-t border-zinc-200 px-4 py-3">
          <div className="flex items-center gap-1">
            <button
              onClick={() => onNavigate?.('templates')}
              className={`flex h-8 w-8 items-center justify-center rounded-[7px] ${
                activePage === 'templates' ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-500 hover:bg-zinc-200'
              }`}
            >
              <span className="text-[13px] font-semibold">/</span>
            </button>
            <button
              onClick={() => onOrganizeOpen?.()}
              className="flex h-8 w-8 items-center justify-center rounded-[7px] bg-zinc-100 text-zinc-500 hover:bg-zinc-200"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" />
                <line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" />
                <line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" />
                <line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" />
                <line x1="17" y1="16" x2="23" y2="16" />
              </svg>
            </button>
          </div>
          <button
            onClick={() => onNavigate?.('settings')}
            title="Your account"
            className={`flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-semibold transition-colors ${
              activePage === 'settings'
                ? 'bg-zinc-900 text-white ring-2 ring-zinc-900/20'
                : 'bg-blue-100 text-blue-600 hover:ring-2 hover:ring-blue-200'
            }`}
          >
            {initials || 'U'}
          </button>
        </div>
      </aside>
  )
}

